import React from 'react';
import { ExternalLink, Instagram, Play } from 'lucide-react';
import { portfolioImages, companyInfo, instagramVideos } from '../data/mock';

const PortfolioSection = () => {
  return (
    <section id="portfolio" className="bg-black py-24 lg:py-32">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-[7.6923%]">
        {/* Section header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-[2px] bg-electric-yellow" />
              <span className="text-electric-yellow text-sm font-medium tracking-widest uppercase">
                Our Work
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white">
              Recent Projects
            </h2>
          </div>
          
          <a
            href={companyInfo.instagram}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-electric-yellow hover:text-white transition-colors duration-300"
          >
            <Instagram size={20} />
            <span className="font-medium">Follow on Instagram</span>
            <ExternalLink size={16} />
          </a>
        </div>

        {/* Portfolio grid */}
        <div className="grid md:grid-cols-2 gap-6">
          {portfolioImages.map((image, index) => (
            <div 
              key={image.id}
              className="group relative aspect-[4/3] overflow-hidden bg-neutral-900"
            >
              <img
                src={image.src}
                alt={image.alt}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              
              {/* Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              
              {/* Border glow effect */}
              <div className="absolute inset-0 border-2 border-electric-yellow/0 group-hover:border-electric-yellow/50 transition-all duration-500" />
              
              {/* Caption */}
              <div className="absolute bottom-0 left-0 right-0 p-6 transform translate-y-full group-hover:translate-y-0 transition-transform duration-500">
                <p className="text-white font-medium">{image.alt}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Instagram Video Embeds Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
          {instagramVideos.map((video) => (
            <a
              key={video.id}
              href={video.url}
              target="_blank"
              rel="noopener noreferrer"
              className="group relative aspect-square bg-neutral-900/50 border border-white/5 overflow-hidden hover:border-electric-yellow/30 transition-all duration-500"
            >
              {/* Instagram embed iframe */}
              <iframe
                src={video.embedUrl}
                className="w-full h-full border-0 pointer-events-none"
                allowTransparency="true"
                scrolling="no"
                title={`Instagram post ${video.id}`}
                style={{ 
                  transform: 'scale(1.5)',
                  transformOrigin: 'center center',
                  marginTop: '-20%'
                }}
              />
              
              {/* Overlay with play button */}
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="w-14 h-14 rounded-full bg-electric-yellow/20 border-2 border-electric-yellow flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <Play size={24} className="text-electric-yellow ml-1" fill="currentColor" />
                </div>
              </div>
              
              {/* Electric border animation on hover */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
                <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-electric-yellow to-transparent animate-electric-horizontal" />
                <div className="absolute bottom-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-electric-yellow to-transparent animate-electric-horizontal-reverse" />
              </div>
              
              {/* Instagram icon badge */}
              <div className="absolute bottom-2 right-2 bg-black/60 p-1.5 rounded opacity-70 group-hover:opacity-100 transition-opacity">
                <Instagram size={14} className="text-white" />
              </div>
            </a>
          ))}
        </div>
        
        {/* View All CTA */}
        <div className="mt-8 text-center">
          <a
            href={companyInfo.instagram}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 text-neutral-400 hover:text-electric-yellow transition-colors duration-300"
          >
            <Instagram size={18} />
            <span>View all projects on Instagram</span>
            <ExternalLink size={14} />
          </a>
        </div>
      </div>
    </section>
  );
};

export default PortfolioSection;
